import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, CheckCircle2, FileEdit, AlertTriangle, XCircle, IndianRupee, ImagePlus, Images } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminDashboard } from "../../services/api";
import { formatINR } from "../../data/content";

const CARDS = [
  { key: "total_products", label: "Total products", icon: Package, tone: "text-brand-charcoal" },
  { key: "published_products", label: "Published", icon: CheckCircle2, tone: "text-brand-orange" },
  { key: "draft_products", label: "Drafts", icon: FileEdit, tone: "text-brand-gold" },
  { key: "low_stock_products", label: "Low stock", icon: AlertTriangle, tone: "text-amber-600" },
  { key: "out_of_stock_products", label: "Out of stock", icon: XCircle, tone: "text-red-600" },
  { key: "total_inventory_value", label: "Inventory value", icon: IndianRupee, tone: "text-brand-charcoal", money: true },
];

export default function AdminDashboard() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!ready || user?.role !== "admin") return;
    setError("");
    adminDashboard().then(setStats).catch((err) => setError(err?.response?.status === 401 ? "Your session has expired. Please sign in again." : "Could not load dashboard stats."));
  }, [ready, user, attempt]);

  return (
    <AdminLayout title="Dashboard" subtitle="Catalogue & inventory at a glance">
      <div data-testid="admin-dashboard">
        {error && (
          <p className="mb-6 flex items-center gap-4 text-sm text-red-600" data-testid="dashboard-error">
            {error}
            <button type="button" onClick={() => setAttempt((a) => a + 1)} data-testid="dashboard-retry" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-orange underline-offset-4 hover:underline">Retry</button>
          </p>
        )}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {CARDS.map(({ key, label, icon: Icon, tone, money }) => (
            <div
              key={key}
              data-testid={`stat-${key}`}
              className="group border border-brand-sand bg-brand-surface p-6 transition-shadow hover:shadow-[0_12px_40px_-24px_rgba(33,26,23,0.5)]"
            >
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-stone">{label}</p>
                <Icon className={`h-5 w-5 ${tone}`} />
              </div>
              <p className={`mt-5 font-serif text-4xl tracking-tight ${tone}`}>
                {stats ? (money ? formatINR(stats[key] || 0) : stats[key] ?? 0) : "—"}
              </p>
            </div>
          ))}
        </div>

        {stats?.photo_coverage && (
          <section className="mt-10 border border-brand-sand bg-brand-surface p-6" data-testid="photo-coverage">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-stone">
                  <Images className="h-4 w-4 text-brand-gold" /> Photo coverage
                </p>
                <p className="mt-2 font-serif text-2xl text-brand-charcoal" data-testid="photo-coverage-summary">
                  {stats.photo_coverage.with_photos} of {stats.photo_coverage.total} bracelets photographed
                </p>
              </div>
              <Link to="/admin/bulk-images" data-testid="photo-coverage-bulk-link" className="flex items-center gap-2 border border-brand-charcoal/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange">
                <ImagePlus className="h-4 w-4" /> Bulk photos
              </Link>
            </div>
            <div className="mt-5 h-1.5 bg-brand-sand" data-testid="photo-coverage-bar">
              <div
                className="h-full bg-brand-gold transition-all duration-500"
                style={{ width: `${stats.photo_coverage.total ? (stats.photo_coverage.with_photos / stats.photo_coverage.total) * 100 : 0}%` }}
              />
            </div>
            <div className="mt-6 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
              {stats.photo_coverage.categories.map((c) => {
                const pct = c.total ? Math.round((c.with_photos / c.total) * 100) : 0;
                return (
                  <Link
                    key={c.slug}
                    to={`/admin/products?category=${c.slug}`}
                    data-testid={`photo-coverage-${c.slug}`}
                    className="group flex items-center gap-4 py-1.5"
                  >
                    <span className="w-40 shrink-0 truncate text-sm text-brand-charcoal group-hover:text-brand-orange">{c.name}</span>
                    <span className="h-1 flex-1 bg-brand-sand">
                      <span className={`block h-full ${pct === 0 ? "bg-red-300" : pct < 100 ? "bg-brand-gold" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </span>
                    <span className={`w-20 shrink-0 text-right text-xs ${pct === 0 ? "text-amber-700" : "text-brand-stone"}`}>
                      {c.with_photos}/{c.total} · {pct}%
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-10 flex flex-wrap gap-3">          <Link to="/admin/products/new" data-testid="dashboard-add-product"
            className="bg-brand-charcoal px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:bg-brand-orange">
            Add product
          </Link>
          <Link to="/admin/inventory" data-testid="dashboard-manage-stock"
            className="border border-brand-sand px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange">
            Manage stock
          </Link>
          <Link to="/admin/categories" data-testid="dashboard-manage-categories"
            className="border border-brand-sand px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange">
            Manage categories
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
